// © 2025 Camp Promethean, LLC. All Rights Reserved.

import {
  UserRound,
  Settings,
  Building2,
  Plug,
  Bookmark,
  LogOut,
  CalendarDays,
  Truck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { resolveUiSkin } from "@/lib/uiSkin";
import { isPlatformAdmin } from "@/lib/platformAdmin";

const userMenuItems = [
  { label: "My consultations", icon: CalendarDays, path: "/agora/professionals/my-consultations" },
  { label: "Profile", icon: UserRound, path: "/profile" },
  { label: "Account Settings", icon: Settings, path: "/profile?tab=preferences" },
  { label: "Quick Access", icon: Bookmark, path: "/profile?tab=quick-access" },
  { label: "Delivery & privacy", icon: Truck, path: "/profile?tab=delivery" },
  { label: "Facilities", icon: Building2, path: "/profile/facilities" },
  { label: "Integrations", icon: Plug, path: "/profile/integrations" },
];

export function SidebarUserMenu() {
  const { profile, logout } = useAuth();
  const navigate = useNavigate();
  const isAthenaSkin = resolveUiSkin(profile?.id) === "athena";
  const isAdmin = isPlatformAdmin(profile?.role);

  const visibleUserMenuItems = userMenuItems.filter(
    (item) => isAdmin || !item.path.startsWith("/agora"),
  );

  const userInitials =
    profile?.first_name && profile?.last_name
      ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
      : "";

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className={cn(
              "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
              isAthenaSkin && "hover:bg-neutral-800",
            )}
          >
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-lg border text-xs font-medium",
                isAthenaSkin
                  ? "border-neutral-700 bg-neutral-800 text-neutral-200"
                  : "border-border bg-primary/10 text-primary",
              )}
            >
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt=""
                  className="h-full w-full rounded-lg object-cover"
                />
              ) : (
                userInitials || <UserRound className="h-4 w-4" />
              )}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">
                {profile?.first_name
                  ? `${profile.first_name} ${profile.last_name ?? ""}`.trim()
                  : "Account"}
              </span>
              {profile?.membership_tier && profile.membership_tier !== "free" && (
                <span className="truncate text-xs capitalize text-muted-foreground">
                  {profile.membership_tier}
                </span>
              )}
            </div>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-56">
          {visibleUserMenuItems.map((item) => (
            <DropdownMenuItem
              key={item.label}
              onClick={() => navigate(item.path)}
              className="cursor-pointer"
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="cursor-pointer">
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}
